import React from "react";
import {
  ArrowUpRight,
  HeartPulse,
  Mail,
  MessageCircle,
  Phone,
  ShieldCheck,
  Stethoscope,
} from "lucide-react";

const footerColumns = [
  {
    title: "Product",
    links: [
      { name: "How it works", url: "/#how-it-works" },
      { name: "For Doctors", url: "/#for-doctors" },
      { name: "Features", url: "/#features" },
      { name: "Pricing", url: "/#pricing" },
    ],
  },
  {
    title: "Company",
    links: [
      { name: "About", url: "/#about" },
      { name: "Contact", url: "/#contact" },
      { name: "Doctor Registration", url: "/register-as-doctor" },
    ],
  },
  {
    title: "Support",
    links: [
      { name: "Help Center", url: "/help" },
      { name: "Privacy Policy", url: "/privacy" },
      { name: "Terms of Service", url: "/terms" },
    ],
  },
];

const Footer = () => {
  const handleScroll = (e, url) => {
    if (url.startsWith("/#")) {
      e.preventDefault();

      const id = url.replace("/#", "");
      const el = document.getElementById(id);

      if (el) {
        const yOffset = -90;
        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <footer className="relative overflow-hidden bg-[#062b25] text-white">
      {/* Background glows */}
      <div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-[#25D366]/15 blur-3xl" />
      <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-cyan-300/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 pb-8 pt-16 sm:px-6 lg:px-8 lg:pt-20">
        {/* Top CTA Strip */}
        <div className="mb-14 rounded-[2rem] border border-white/10 bg-white/[0.06] p-5 shadow-[0_24px_70px_rgba(0,0,0,0.18)] backdrop-blur-xl sm:p-7">
          <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="flex gap-4">
              <div className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#25D366]/15 text-[#25D366] sm:flex">
                <Stethoscope size={25} />
              </div>

              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-[#25D366]">
                  Join the doctor network
                </p>

                <h3 className="mt-2 text-2xl font-black tracking-[-0.04em] text-white sm:text-3xl">
                  Practice smarter with Zaviramed.
                </h3>

                <p className="mt-2 max-w-2xl text-sm font-medium leading-7 text-white/65">
                  Consult patients from WhatsApp, manage cases from your portal,
                  and deliver care with a structured digital workflow.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href="/register-as-doctor"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-white px-5 text-sm font-black text-[#075E54] transition hover:-translate-y-0.5 hover:bg-emerald-50"
              >
                Join as Doctor
                <ArrowUpRight size={16} />
              </a>

              <button
                type="button"
                onClick={() =>
                  window.open("https://wa.me/2348077417209", "_blank")
                }
                className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-5 text-sm font-black text-white transition hover:-translate-y-0.5 hover:bg-white/15"
              >
                <MessageCircle size={17} />
                Chat on WhatsApp
              </button>
            </div>
          </div>
        </div>

        {/* Main footer */}
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1.8fr]">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-13 w-13 items-center justify-center rounded-2xl bg-white shadow-lg shadow-black/10">
                <img
                  src="/zavira_nobg.png"
                  alt="Zaviramed"
                  className="h-10 w-10 object-contain"
                />
              </div>

              <div>
                <h2 className="text-xl font-black tracking-[-0.04em] text-white">
                  Zavira<span className="text-[#25D366]">Med</span>
                </h2>
                <p className="text-xs font-bold text-white/45">
                  WhatsApp-first healthcare
                </p>
              </div>
            </div>

            <p className="mt-5 max-w-sm text-sm font-medium leading-7 text-white/60">
              Zaviramed helps doctors consult patients remotely through a
              WhatsApp-first telemedicine workflow supported by a secure doctor
              dashboard.
            </p>

            <div className="mt-6 grid max-w-sm gap-3">
              <a
                href="https://wa.me/2348077417209"
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 transition hover:bg-white/[0.08]"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#25D366]/15 text-[#25D366]">
                  <MessageCircle size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-black text-white">
                    WhatsApp Support
                  </p>
                  <p className="truncate text-xs font-semibold text-white/45">
                    Chat with the Zaviramed team
                  </p>
                </div>
              </a>

              <a
                href="tel:09160340807"
                className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 transition hover:bg-white/[0.08]"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white">
                  <Phone size={18} />
                </div>
                <div>
                  <p className="text-xs font-black text-white">Call Us</p>
                  <p className="text-xs font-semibold text-white/45">
                    0916 034 0807
                  </p>
                </div>
              </a>

              <a
                href="mailto:support@zaviramed.com.ng"
                className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 transition hover:bg-white/[0.08]"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white">
                  <Mail size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-black text-white">Email</p>
                  <p className="truncate text-xs font-semibold text-white/45">
                    support@zaviramed.com.ng
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* Links */}
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3">
            {footerColumns.map((col) => (
              <div key={col.title}>
                <h4 className="text-sm font-black text-white">{col.title}</h4>

                <ul className="mt-5 space-y-3">
                  {col.links.map((link) => (
                    <li key={link.name}>
                      <a
                        href={link.url}
                        onClick={(e) => handleScroll(e, link.url)}
                        className="text-sm font-semibold text-white/50 transition hover:text-[#25D366]"
                      >
                        {link.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-14 grid gap-3 border-t border-white/10 pt-8 sm:grid-cols-3">
          {[
            {
              icon: ShieldCheck,
              title: "Secure access",
              text: "Doctor dashboard built for patient care workflows.",
            },
            {
              icon: HeartPulse,
              title: "Clinical support",
              text: "Designed around consultations, history, and follow-up.",
            },
            {
              icon: MessageCircle,
              title: "WhatsApp-first",
              text: "Patients start from the channel they already use.",
            },
          ].map((item) => {
            const Icon = item.icon;

            return (
              <div
                key={item.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"
              >
                <Icon size={19} className="text-[#25D366]" />
                <p className="mt-3 text-sm font-black text-white">
                  {item.title}
                </p>
                <p className="mt-1 text-xs font-medium leading-6 text-white/45">
                  {item.text}
                </p>
              </div>
            );
          })}
        </div>

        {/* Bottom */}
        <div className="mt-8 flex flex-col gap-3 border-t border-white/10 pt-6 text-sm font-semibold text-white/40 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Zaviramed. All rights reserved.</p>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href="https://zaviramed.com.ng"
              target="_blank"
              rel="noreferrer"
              className="text-white/50 transition hover:text-[#25D366]"
            >
              zaviramed.com.ng
            </a>

            <span className="hidden text-white/20 sm:inline">•</span>

            <a
              href="/privacy"
              className="text-white/50 transition hover:text-[#25D366]"
            >
              Privacy
            </a>

            <span className="hidden text-white/20 sm:inline">•</span>

            <a
              href="/terms"
              className="text-white/50 transition hover:text-[#25D366]"
            >
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;